import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Play, Loader2, CheckCircle2, XCircle, Globe, Pause, PlayCircle, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { triggerRagGeneration, triggerAllRagGenerations, type RagTriggerResult } from '@/services/rag/ragTriggerService';
import { RagSchedulingService } from '@/services/rag/ragSchedulingService';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

interface RegionStatus {
  region: string;
  displayName: string;
  status: 'idle' | 'loading' | 'success' | 'error';
  message?: string;
  draftId?: string;
}

const REGIONS: { region: string; displayName: string }[] = [
  { region: 'europa', displayName: 'Europa' },
  { region: 'nord_amerika', displayName: 'Nord-Amerika' },
  { region: 'asia', displayName: 'Asia' },
  { region: 'latin_amerika', displayName: 'Latin-Amerika' },
  { region: 'midtosten', displayName: 'Midtøsten' },
  { region: 'afrika', displayName: 'Afrika' },
  { region: 'skandinavia', displayName: 'Skandinavia' },
];

export const RagManualTrigger: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [regionStatuses, setRegionStatuses] = useState<Record<string, RegionStatus>>(
    REGIONS.reduce((acc, r) => ({
      ...acc,
      [r.region]: { region: r.region, displayName: r.displayName, status: 'idle' }
    }), {})
  );
  const [isGeneratingAll, setIsGeneratingAll] = useState(false);

  // Fetch scheduled tasks
  const { data: scheduledTasks, isLoading: tasksLoading } = useQuery({
    queryKey: ['rag-scheduled-tasks'],
    queryFn: () => RagSchedulingService.getAllRegionTasks(),
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  // Toggle mutation
  const toggleMutation = useMutation({
    mutationFn: ({ region, enable }: { region: string; enable: boolean }) =>
      RagSchedulingService.toggleRegionStatus(region, enable),
    onSuccess: (success, { region, enable }) => {
      if (success) {
        queryClient.invalidateQueries({ queryKey: ['rag-scheduled-tasks'] });
        toast.success(`${region} automatisk generering ${enable ? 'aktivert' : 'pauset'}`);
      } else {
        toast.error('Kunne ikke endre status. Prøv igjen.');
      }
    },
  });

  const activeCount = scheduledTasks 
    ? Object.values(scheduledTasks).filter(t => t.status === 'active').length 
    : 0;
  const allActive = activeCount === 7;

  const updateRegionStatus = (region: string, updates: Partial<RegionStatus>) => {
    setRegionStatuses(prev => ({
      ...prev,
      [region]: { ...prev[region], ...updates }
    }));
  };

  const handleGenerateRegion = async (region: string, displayName: string) => {
    updateRegionStatus(region, { status: 'loading' });
    toast.info(`Starter generering for ${displayName}...`);

    const result = await triggerRagGeneration(region);

    if (result.success) {
      updateRegionStatus(region, {
        status: 'success',
        message: result.message,
        draftId: result.draftId
      });
      toast.success(`${displayName}: Artikkel generert!`, {
        description: 'Se Redaksjonell Godkjenning for å godkjenne.'
      });
    } else {
      updateRegionStatus(region, {
        status: 'error',
        message: result.error
      });
      toast.error(`${displayName}: Feil under generering`, {
        description: result.error
      });
    }
  };

  const handleGenerateAll = async () => {
    setIsGeneratingAll(true);
    toast.info('Starter generering for alle regioner...');

    // Reset all statuses
    REGIONS.forEach(r => {
      updateRegionStatus(r.region, { status: 'idle', message: undefined, draftId: undefined });
    });

    const results = await triggerAllRagGenerations();

    results.forEach((result: RagTriggerResult) => {
      const region = REGIONS.find(r => r.region === result.region);
      if (!region) return;

      if (result.success) {
        updateRegionStatus(result.region, {
          status: 'success',
          message: result.message,
          draftId: result.draftId
        });
      } else {
        updateRegionStatus(result.region, {
          status: 'error',
          message: result.error
        });
      }
    });

    const successCount = results.filter(r => r.success).length;
    const failCount = results.filter(r => !r.success).length;

    if (failCount === 0) {
      toast.success(`Alle ${successCount} artikler generert!`, {
        description: 'Se Redaksjonell Godkjenning for å godkjenne.'
      });
    } else {
      toast.warning(`${successCount} av ${results.length} artikler generert`, {
        description: `${failCount} feilet. Se detaljer nedenfor.`
      });
    }

    setIsGeneratingAll(false);
  };

  const handleToggleAutomation = async (region: string, currentStatus: string) => {
    const enable = currentStatus === 'paused';
    toggleMutation.mutate({ region, enable });
  };

  const handleToggleAllAutomation = async (enable: boolean) => {
    toast.info(`${enable ? 'Aktiverer' : 'Pauser'} automatisk generering for alle regioner...`);
    
    for (const region of REGIONS.map(r => r.region)) {
      await RagSchedulingService.toggleRegionStatus(region, enable);
    }
    
    queryClient.invalidateQueries({ queryKey: ['rag-scheduled-tasks'] });
    toast.success(`Automatisk generering ${enable ? 'aktivert' : 'pauset'} for alle regioner`);
  };

  const getStatusIcon = (status: RegionStatus['status']) => {
    switch (status) {
      case 'loading':
        return <Loader2 className="w-4 h-4 animate-spin text-blue-500" />;
      case 'success':
        return <CheckCircle2 className="w-4 h-4 text-green-500" />;
      case 'error':
        return <XCircle className="w-4 h-4 text-red-500" />;
      default:
        return <Globe className="w-4 h-4 text-muted-foreground" />;
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Play className="w-5 h-5" />
          RAG Manuell Trigger
        </CardTitle>
        <CardDescription>
          Generer RAG-artikler manuelt utenfor planlagte tider. Nyttig for testing og ad-hoc publisering.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Automatic Generation Controls */}
        <div className="flex justify-between items-center pb-4 border-b">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <p className="font-medium">Automatisk generering</p>
              {scheduledTasks && (
                <Badge variant={allActive ? 'default' : 'secondary'}>
                  {activeCount} av 7 aktive
                </Badge>
              )}
            </div>
            <p className="text-sm text-muted-foreground">
              Kontroller automatisk generering for alle regioner
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => handleToggleAllAutomation(true)}
              disabled={toggleMutation.isPending || allActive}
              variant="outline"
              size="sm"
            >
              <PlayCircle className="w-4 h-4 mr-2" />
              Aktiver alle
            </Button>
            <Button
              onClick={() => handleToggleAllAutomation(false)}
              disabled={toggleMutation.isPending || activeCount === 0}
              variant="outline"
              size="sm"
            >
              <Pause className="w-4 h-4 mr-2" />
              Pause alle
            </Button>
          </div>
        </div>

        {/* Generate All Button */}
        <div className="flex justify-between items-center pb-4 border-b">
          <div>
            <p className="font-medium">Generer alle regioner manuelt</p>
            <p className="text-sm text-muted-foreground">
              Kjører alle 7 regioner sekvensielt (ca. 2-3 min per region)
            </p>
          </div>
          <Button
            onClick={handleGenerateAll}
            disabled={isGeneratingAll}
            variant="default"
            size="sm"
          >
            {isGeneratingAll ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Genererer...
              </>
            ) : (
              <>
                <Play className="w-4 h-4 mr-2" />
                Generer alle
              </>
            )}
          </Button>
        </div>

        {/* Loading state for tasks */}
        {tasksLoading && (
          <div className="text-center py-4 text-sm text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin inline mr-2" />
            Henter automatisk status...
          </div>
        )}

        {/* Individual Region Controls */}
        <div className="space-y-3">
          {REGIONS.map(({ region, displayName }) => {
            const status = regionStatuses[region];
            const task = scheduledTasks?.[region];
            const isAutoActive = task?.status === 'active';
            
            return (
              <div
                key={region}
                className={`flex items-center justify-between p-3 border rounded-lg hover:bg-accent/50 transition-colors ${
                  isAutoActive 
                    ? 'border-green-500/50 bg-green-50/30 dark:bg-green-950/20' 
                    : ''
                }`}
              >
                <div className="flex items-center gap-3 flex-1">
                  {getStatusIcon(status.status)}
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-medium">{displayName}</p>
                      {task && (
                        <Badge 
                          variant={isAutoActive ? 'default' : 'secondary'}
                          className={isAutoActive ? 'bg-green-600 dark:bg-green-700' : 'bg-gray-500 dark:bg-gray-600'}
                        >
                          {isAutoActive ? 'Automatisk aktiv' : 'Pauset'}
                        </Badge>
                      )}
                    </div>
                    
                    {task?.next_run && (
                      <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                        <Clock className="w-3 h-3" />
                        <span>
                          Neste kjøring: {RagSchedulingService.formatNextRun(task.next_run)}
                        </span>
                      </div>
                    )}
                    
                    {status.message && (
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {status.message}
                      </p>
                    )}
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  {/* Toggle automatic generation */}
                  {task && (
                    <Button
                      onClick={() => handleToggleAutomation(region, task.status)}
                      disabled={toggleMutation.isPending}
                      variant="outline"
                      size="sm"
                    >
                      {isAutoActive ? (
                        <>
                          <Pause className="w-4 h-4 mr-2" />
                          Pause
                        </>
                      ) : (
                        <>
                          <PlayCircle className="w-4 h-4 mr-2" />
                          Aktiver
                        </>
                      )}
                    </Button>
                  )}
                  
                  {/* Manual trigger */}
                  <Button
                    onClick={() => handleGenerateRegion(region, displayName)}
                    disabled={status.status === 'loading' || isGeneratingAll}
                    variant="outline"
                    size="sm"
                  >
                    {status.status === 'loading' ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Genererer...
                      </>
                    ) : (
                      <>
                        <Play className="w-4 h-4 mr-2" />
                        Generer
                      </>
                    )}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Link to Editorial Approval */}
        <div className="pt-4 border-t">
          <Button
            onClick={() => navigate('/editorial-approval')}
            variant="secondary"
            className="w-full"
          >
            Gå til Redaksjonell Godkjenning →
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
